import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Pagination,
  Container,
  Grid,
} from '@mui/material';
import { 
  Visibility,
  Refresh as RefreshIcon,
  Inventory as PackageIcon,
  Schedule as ClockIcon,
  RemoveRedEye as EyeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface Quote {
  id: number;
  reference: string;
  sender_name: string;
  sender_phone: string;
  recipient_name: string;
  recipient_phone: string;
  package_type: string;
  weight: number;
  zone_name: string;
  total_price: number;
  currency: string;
  status: 'pending' | 'reviewed' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

interface QuoteListResponse {
  quotes: Quote[];
  total: number;
  page: number;
  limit: number;
}

const QuoteList: React.FC = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation();

  const limit = 10;

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      let url = `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/quotes?page=${page}&limit=${limit}`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch quotes');
      }

      const data: QuoteListResponse = await response.json();
      setQuotes(data.quotes || []);
      setTotalPages(Math.ceil(data.total / limit));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, [page, statusFilter]);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'warning' as const, icon: ClockIcon },
      reviewed: { color: 'info' as const, icon: EyeIcon },
      approved: { color: 'success' as const, icon: CheckCircleIcon },
      rejected: { color: 'error' as const, icon: XCircleIcon },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Chip
        icon={<Icon />}
        label={t(`quotes.status.${status}`)}
        color={config.color}
        size="small"
      />
    );
  };

  const filteredQuotes = quotes.filter(quote =>
    quote.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quote.sender_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quote.recipient_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusStats = () => {
    const stats = quotes.reduce((acc, quote) => {
      acc[quote.status] = (acc[quote.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      pending: stats.pending || 0,
      reviewed: stats.reviewed || 0,
      approved: stats.approved || 0,
      rejected: stats.rejected || 0,
      total: quotes.length,
    };
  };

  const stats = getStatusStats();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <RefreshIcon className="animate-spin" />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('quotes.list.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('quotes.list.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={fetchQuotes}
          startIcon={<RefreshIcon />}
        >
          {t('common.refresh')}
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <PackageIcon color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {stats.total}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <ClockIcon color="warning" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('quotes.status.pending')}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="warning.main">
                    {stats.pending}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <EyeIcon color="info" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('quotes.status.reviewed')}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="info.main">
                    {stats.reviewed}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <CheckCircleIcon color="success" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('quotes.status.approved')}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {stats.approved}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <XCircleIcon color="error" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Rejected
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="error.main">
                    {stats.rejected}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder={t('quotes.filters.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('quotes.filters.status')}</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label={t('quotes.filters.status')}
                >
                  <MenuItem value="all">{t('quotes.status.all')}</MenuItem>
                  <MenuItem value="pending">{t('quotes.status.pending')}</MenuItem>
                  <MenuItem value="reviewed">{t('quotes.status.reviewed')}</MenuItem>
                  <MenuItem value="approved">{t('quotes.status.approved')}</MenuItem>
                  <MenuItem value="rejected">{t('quotes.status.rejected')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Card sx={{ mb: 3, borderColor: 'error.main' }}>
          <CardContent>
            <Typography color="error">{t('quotes.list.error')}: {error}</Typography>
          </CardContent>
        </Card>
      )}

      {/* Quotes Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('quotes.title')} ({filteredQuotes.length})
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('quotes.detail.reference')}</TableCell>
                  <TableCell>{t('quotes.sender.title')}</TableCell>
                  <TableCell>{t('quotes.recipient.title')}</TableCell>
                  <TableCell>{t('quotes.package.title')}</TableCell>
                  <TableCell>{t('quotes.delivery.zone')}</TableCell>
                  <TableCell>{t('quotes.pricing.totalPrice')}</TableCell>
                  <TableCell>{t('quotes.detail.status')}</TableCell>
                  <TableCell>{t('quotes.detail.createdAt')}</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQuotes.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {quote.reference}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {quote.sender_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {quote.sender_phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {quote.recipient_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {quote.recipient_phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {quote.package_type}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {quote.weight}kg
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{quote.zone_name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {quote.total_price.toLocaleString()} {quote.currency}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(quote.status)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(quote.created_at), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/quotes/${quote.id}`)}
                        startIcon={<EyeIcon />}
                      >
                        {t('quotes.actions.view')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredQuotes.length === 0 && (
              <Box textAlign="center" py={4}>
                <PackageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">{t('quotes.list.noQuotes')}</Typography>
              </Box>
            )}
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            variant="outlined"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            sx={{ mr: 2 }}
          >
            {t('quotes.pagination.previous')}
          </Button>
          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
            {t('quotes.pagination.page')} {page} {t('quotes.pagination.of')} {totalPages}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
          >
            {t('quotes.pagination.next')}
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default QuoteList;
